import { Box, Checkbox, type CheckboxProps, Typography } from '@mui/material';
import type { GridApiCommon, GridRowSelectionModel } from '@mui/x-data-grid';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import { GridRow } from '@mui/x-data-grid';
import type { InvoiceOverview } from '@vy/api-definition';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import {
  extract,
  isEmptyArray,
  isNotNullOrUndefined,
  isNullOrUndefined,
  sumBy,
  uniq,
  validateNotNullOrUndefined,
} from '@vy/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { CalendarDropdown } from '../../../../../components/design/calendar-dropdown/CalendarDropdown';
import { flattenCustomFieldValuesIntoRow } from '@vy/custom-fields-display';
import { useInvoicesTableColumns } from './use-invoices-table-columns';
import { VyTableWithFilters } from '../../../../../components/new-design/table/vy-table-with-filters';
import type { VyTableMinimalProps } from '../../../../../components/new-design/table/vy-table/vy-table-types';
import { useAccount } from '../../../../../hooks/requests/use-account';
import { getCurrentDate } from '../../../../../utils/date';
import { convertCurrencyToUsd, formatPrice } from '@vy/services';
import { CURRENCIES, type CurrencyExchangeRates } from '@vy/types';
import { toast } from 'react-toastify';
import type { useInvoicesFilters } from '../../../hooks/use-invoices-filters';
import { INVOICES_COLUMNS_KEYS } from '../InvoiceOverviewTable/invoice-overview-table-consts/invoice-columns';
import InvoiceTableEmptyState from '../InvoiceOverviewTable/InvoiceTableEmptyState';
import { InvoicesHighlights } from '../InvoicesHighlights/InvoicesHighlights';
import { useInvoicesFilterFields } from '../../invoices-page-utils/use-invoices-filter-fields';
import type { FilterLineType } from '../../../../../components/new-design/vyTableFilter';

type InvoicesTableProps = VyTableMinimalProps<InvoiceOverview> & {
  apiRef: React.MutableRefObject<GridApiCommon | null>;
  filters?: ReturnType<typeof useInvoicesFilters>;
  onInvoicesSelect: (selectedInvoices: InvoiceOverview[]) => void;
  exchangeRates?: CurrencyExchangeRates;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onDownload?: () => void | Promise<void>;
  actions: React.ReactNode;
  invoicesHighlightsSummary?: React.ComponentProps<typeof InvoicesHighlights>['invoicesHighlightsSummary'];
  availableCustomFieldNames?: string[];
};

export const InvoicesTable: React.FC<InvoicesTableProps> = ({
  apiRef,
  tableName,
  filters,
  onInvoicesSelect,
  exchangeRates,
  onDownload,
  actions,
  invoicesHighlightsSummary,
  availableCustomFieldNames,
  ...props
}) => {
  const invoicesFilterFields = useInvoicesFilterFields();

  const { data } = useAccount({ suspense: true });
  const account = data?.account;
  validateNotNullOrUndefined(account, 'account');

  const [selectedInvoicesIds, setSelectedInvoicesIds] = useState<string[]>([]);

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const ids = Array.from(model.ids).map((key) => key.toString());
      setSelectedInvoicesIds(ids);

      const selectedRowsArr = ids.map((id) => apiRef.current?.getRow(id)).filter(isNotNullOrUndefined);
      onInvoicesSelect(selectedRowsArr);
    },
    [apiRef, onInvoicesSelect],
  );

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type: 'include', ids: new Set(props.loadingItems ? [] : (selectedInvoicesIds ?? [])) }),
    [props.loadingItems, selectedInvoicesIds],
  );

  const rowsWithFlattenedCustomFields = useMemo(
    () => (props.items ?? []).map(flattenCustomFieldValuesIntoRow),
    [props.items],
  );

  const columns = useInvoicesTableColumns({
    account,
    tableName,
    items: props.items,
    availableCustomFieldNames,
  });

  const columnVisibilityModel = useMemo(
    () => ({
      customerExternalId: false,
      customerErpId: false,
      customerCrmId: false,
      purchaseOrder: false,
      notes: false,
      marketplaceRawResponse: false,
    }),
    [],
  );

  const selectedInvoices = useMemo(() => {
    const api = apiRef?.current;
    if (isNullOrUndefined(api)) {
      return [];
    }

    return selectedInvoicesIds.map((id) => api.getRow(id)).filter(isNotNullOrUndefined);
  }, [selectedInvoicesIds, apiRef]);

  const selectedInvoicesStatsResult = useMemo(() => {
    const invoicesCurrencies = extract(selectedInvoices, 'currency');
    const uniqueCurrencies = uniq(invoicesCurrencies);

    if (isEmptyArray(uniqueCurrencies) || isNullOrUndefined(exchangeRates)) {
      return { stats: null, error: null };
    }

    const errors: string[] = [];
    const totalUsd = sumBy(selectedInvoices, ({ revenueBreakdown, currency }) => {
      try {
        return convertCurrencyToUsd(revenueBreakdown.total, currency, exchangeRates);
      } catch (error: unknown) {
        errors.push((error as Error).message);
        return 0;
      }
    });

    return {
      stats: { currency: CURRENCIES.Usd, total: totalUsd },
      error: errors.length > 0 ? errors[0] : null,
    };
  }, [selectedInvoices, exchangeRates]);

  const selectedInvoicesStats = selectedInvoicesStatsResult.stats;

  useEffect(() => {
    if (selectedInvoicesStatsResult.error) {
      toast.error(selectedInvoicesStatsResult.error);
    }
  }, [selectedInvoicesStatsResult.error]);

  const dateRangePicker = useMemo(
    () =>
      filters ? (
        <CalendarDropdown
          defaultSelectedMonths={filters.getDefaultSelectedMonths()}
          defaultSelectedYear={getCurrentDate().getFullYear()}
          onChange={(dates) => filters.handleCalendarChange(dates)}
        />
      ) : undefined,
    [filters],
  );

  const contentBetweenToolbarAndTable = useMemo(
    () =>
      filters ? (
        <Box sx={{
          paddingX: 2
        }}>
          <InvoicesHighlights
            invoicesLoading={props.loadingItems}
            filters={filters.filters}
            dates={filters.selectedDates}
            invoicesCount={props.itemsCount ?? 0}
            invoicesHighlightsSummary={invoicesHighlightsSummary}
            onFilterClick={filters.handleFilterClick}
          />
        </Box>
      ) : undefined,
    [filters, props.loadingItems, props.itemsCount, invoicesHighlightsSummary],
  );

  const tableFooter = useMemo(
    () => (
      <Box
        sx={{
          position: 'absolute',
          left: 20,
          bottom: 16,
          display: 'flex',
          alignItems: 'center',
          columnGap: '12px',
        }}
      >
        <Typography>{`Selected Invoices: ${selectedInvoicesIds?.length ?? 0}`}</Typography>
        {isNotNullOrUndefined(selectedInvoicesStats) && (
          <Typography>
            {`Sum: ${formatPrice(selectedInvoicesStats.total, selectedInvoicesStats.currency)}`}
          </Typography>
        )}
      </Box>
    ),
    [selectedInvoicesIds?.length, selectedInvoicesStats],
  );

  return (
    <VyTableWithFilters
      apiRef={apiRef as unknown as React.MutableRefObject<GridApiPro>}
      tableName={tableName}
      columns={columns}
      tableColumnsApiConfig={{ visibility: columnVisibilityModel }}
      search={
        filters
          ? {
              value: filters.query ?? '',
              onChange: filters.handleSearchInputChange,
              placeholder: 'Name, email, etc...',
            }
          : undefined
      }
      filters={
        filters
          ? {
              fields: invoicesFilterFields,
              onFiltersChange: filters.handleFilterClick,
              defaultFilters: filters.filters as FilterLineType[],
            }
          : undefined
      }
      columnSettings={{
        excludedFields: [INVOICES_COLUMNS_KEYS.ACTION_MENU],
        mandatoryColumnsCount: 2,
      }}
      dateRangePicker={dateRangePicker}
      toolbarOrder={['search', 'dateRangePicker', 'filters']}
      contentBetweenToolbarAndTable={contentBetweenToolbarAndTable}
      tableFooter={tableFooter}
      onDownload={onDownload}
      actions={actions}
      isFilterOpen={filters?.isFilterInvoicesOpen}
      disableRowSelectionOnClick
      rowsPerPageOptions={[10, 25, 50, 100, 500]}
      disableColumnMenu
      noRowsOverlay={() => <InvoiceTableEmptyState />}
      getRowId={(row) => row.id}
      checkboxSelection
      rowSelection
      onRowSelectionModelChange={handleSelectionChange}
      rowSelectionModel={rowSelectionModel}
      slots={{
        baseCheckbox: (baseCheckboxProps) => (
          <Checkbox
            {...(baseCheckboxProps as unknown as CheckboxProps)}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
            }}
            {...getSelectorAttribute(vyTableSelectors.checkbox)}
          />
        ),
        row: (rowProps) => {
          const { id, customer } = rowProps.row;

          return (
            <NavLink to={`/invoices/${id}/customer/${customer.id}/dynamic`}>
              <GridRow {...rowProps} />
            </NavLink>
          );
        },
      }}
      // Use `tableSx` (merged by VyTableWithFilters) rather than `sx`, which
      // would replace VyTableWithFilters's row/cell styling — including the
      // white cell background — and leave the grid showing v9's default
      // (gray) container background.
      tableSx={{
        '& .MuiDataGrid-columnHeaderTitle': {
          fontSize: '14px',
          fontWeight: 580,
          lineHeight: '24px',
          fontFamily: 'Inter, sans-serif',
          color: '#0D1B27DE',
        },
      }}
      {...props}
      items={rowsWithFlattenedCustomFields}
    />
  );
};
